import { useEffect, useState, useRef } from "react";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { Youtube, Upload, Trash2, Download } from "lucide-react";

function money(n) { return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
function inr(n) { return `₹${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function AdminYtRevenue() {
  const [users, setUsers] = useState([]);
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({ user_id: "", year: new Date().getFullYear().toString(), month: String(new Date().getMonth() + 1).padStart(2, "0") });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/yt-revenue").then((r) => setRecords(r.data));
  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(r.data.filter((u) => u.role !== "admin")));
    load();
  }, []);

  const upload = async () => {
    if (!form.user_id || !file) { toast.error("Select user and Excel file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("user_id", form.user_id);
      fd.append("month", `${form.year}-${form.month}`);
      fd.append("file", file);
      const { data } = await api.post("/admin/yt-revenue/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${data.month_label} — ${data.totals?.channels} channels, ${money(data.totals?.total_rev)}`);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/yt-revenue/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const years = ["2023", "2024", "2025", "2026", "2027"];

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Administrator</div>
      <h1 className="mt-2 text-4xl font-bold tracking-tight flex items-center gap-3">
        <Youtube className="w-8 h-8 text-rose-400" /> YouTube Revenue
      </h1>
      <p className="mt-2 text-zinc-500 text-sm">Upload a user's monthly YouTube revenue Excel. Re-uploading for the same user + month replaces the existing report.</p>

      {/* Upload form */}
      <div className="mt-8 p-6 rounded-2xl border border-zinc-800 bg-zinc-950/60 grid md:grid-cols-4 gap-4">
        <F label="User">
          <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
            <SelectTrigger data-testid="yt-upload-user" className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="Select user" /></SelectTrigger>
            <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name} · {u.email}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Year">
          <Select value={form.year} onValueChange={(v) => setForm({ ...form, year: v })}>
            <SelectTrigger data-testid="yt-upload-year" className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Month">
          <Select value={form.month} onValueChange={(v) => setForm({ ...form, month: v })}>
            <SelectTrigger data-testid="yt-upload-month" className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1).padStart(2, "0")}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Excel File (.xlsx)">
          <Input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0])}
            data-testid="yt-upload-file" className="bg-zinc-900 border-zinc-800 file:mr-3 file:rounded-md file:border-0 file:bg-zinc-800 file:text-zinc-200 file:px-2 file:py-1" />
        </F>
        <div className="md:col-span-4 flex justify-end">
          <Button onClick={upload} disabled={uploading} data-testid="yt-upload-btn"
            className="rounded-full bg-gradient-to-r from-rose-400 to-cyan-400 text-zinc-950 font-semibold">
            <Upload className="w-4 h-4 mr-2" /> {uploading ? "Uploading..." : "Upload Report"}
          </Button>
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="p-4">Month</th>
              <th className="p-4">User</th>
              <th className="p-4 text-right">Channels</th>
              <th className="p-4 text-right">Total Rev</th>
              <th className="p-4 text-right">Total INR</th>
              <th className="p-4 text-right">Party</th>
              <th className="p-4">Uploaded</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <tr key={r.id} data-testid={`admin-yt-row-${r.id}`} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                <td className="p-4 font-medium">{r.month_label}</td>
                <td className="p-4 text-sm">{r.user?.full_name}<br /><span className="text-xs text-zinc-500">{r.user?.email}</span></td>
                <td className="p-4 text-right font-mono">{r.totals?.channels || 0}</td>
                <td className="p-4 text-right font-mono text-emerald-400">{money(r.totals?.total_rev)}</td>
                <td className="p-4 text-right font-mono text-cyan-300">{inr(r.totals?.total_inr)}</td>
                <td className="p-4 text-right font-mono text-violet-300">{inr(r.totals?.total_party)}</td>
                <td className="p-4 text-zinc-500 text-sm">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="p-4 text-right">
                  <div className="flex justify-end gap-1">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/yt-revenue/${r.id}/download`} target="_blank" rel="noreferrer">
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-400 hover:text-emerald-400"><Download className="w-4 h-4" /></Button>
                    </a>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-400 hover:text-rose-400" data-testid={`admin-yt-del-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-zinc-950 border-zinc-800">
                        <AlertDialogHeader><AlertDialogTitle>Delete {r.month_label}?</AlertDialogTitle>
                          <AlertDialogDescription>This removes the report from the user's dashboard.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-zinc-900 border-zinc-800">Cancel</AlertDialogCancel>
                          <AlertDialogAction className="bg-rose-500 hover:bg-rose-600" onClick={() => del(r.id)}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>
              </tr>
            ))}
            {records.length === 0 && <tr><td colSpan="8" className="p-12 text-center text-zinc-500">No YouTube revenue reports uploaded yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-zinc-400">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
